import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Card,
  Typography,
  TextField,
  Button,
  Stack,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Work, Subject, ArrowBack, Add, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getTutorByUser, updateTutor, clearTutor } from '../../state/slices/tutor';

import ethiopiaCurriculum from '../../utils/ethiopiaCurriculum.json';

// Use centralized curriculum data
const PREDEFINED_SUBJECTS = ethiopiaCurriculum.subjects;

function TutorExperienceForm() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tutorState = useSelector((state) => state.tutor);
  const userState = useSelector((state) => state.user).user;

  const [formData, setFormData] = useState({
    experience: '',
    subjects: [], // Array of {subject, gradeLevel}
    gradeLevels: [],
  });
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [customSubjectDialogOpen, setCustomSubjectDialogOpen] = useState(false);
  const [customSubject, setCustomSubject] = useState({ name: '', gradeLevel: '' });

  const gradeLevels = ethiopiaCurriculum.gradeLevels;

  useEffect(() => {
    if (userState && userState.roles.includes('TUTOR') && !tutorState.tutor && !tutorState.loading) {
      dispatch(getTutorByUser(userState.id));
    }
  }, [userState, dispatch, tutorState.tutor, tutorState.loading]);

  useEffect(() => {
    if (tutorState.tutor) {
      // Handle both old format (array of strings) and new format (array of objects)
      let subjects = [];
      let loadedGradeLevels = [];
      
      if (tutorState.tutor.subjects && Array.isArray(tutorState.tutor.subjects) && tutorState.tutor.subjects.length > 0) {
        if (typeof tutorState.tutor.subjects[0] === 'string') {
          // Old format - convert to new format
          const firstGradeLevel = tutorState.tutor.gradeLevels && tutorState.tutor.gradeLevels.length > 0
            ? tutorState.tutor.gradeLevels[0]
            : 'All';
          subjects = tutorState.tutor.subjects.map(subject => ({
            subject: String(subject),
            gradeLevel: firstGradeLevel
          }));
          loadedGradeLevels = tutorState.tutor.gradeLevels && tutorState.tutor.gradeLevels.length > 0
            ? tutorState.tutor.gradeLevels
            : [firstGradeLevel];
        } else {
          // New format - strip _id fields if present and ensure proper structure
          subjects = tutorState.tutor.subjects
            .map(subjectObj => {
              // Handle both plain objects and Mongoose documents
              if (!subjectObj || typeof subjectObj !== 'object') {
                return null;
              }
              
              const subject = subjectObj.subject || subjectObj.subjectName || '';
              const gradeLevel = subjectObj.gradeLevel || subjectObj.grade || 'All';
              
              // Return clean object without _id
              if (!subject) {
                return null;
              }
              
              return {
                subject: String(subject),
                gradeLevel: String(gradeLevel)
              };
            })
            .filter(s => s !== null && s.subject && s.gradeLevel); // Filter out invalid entries
          
          // Extract unique grade levels from subjects if gradeLevels is not set
          const subjectGradeLevels = [...new Set(subjects.map(s => s.gradeLevel).filter(Boolean))];
          loadedGradeLevels = Array.isArray(tutorState.tutor.gradeLevels) && tutorState.tutor.gradeLevels.length > 0
            ? tutorState.tutor.gradeLevels.map(g => String(g))
            : subjectGradeLevels;
        }
      } else {
        // No subjects, but check if gradeLevels exist
        loadedGradeLevels = Array.isArray(tutorState.tutor.gradeLevels) && tutorState.tutor.gradeLevels.length > 0
          ? tutorState.tutor.gradeLevels.map(g => String(g))
          : [];
      }

      // Ensure gradeLevels are strings and match the predefined gradeLevels array
      const normalizedGradeLevels = loadedGradeLevels
        .map(g => String(g).trim())
        .filter(g => gradeLevels.includes(g)); // Only include valid grade levels

      setFormData({
        experience: tutorState.tutor.experience || '',
        subjects: subjects,
        gradeLevels: normalizedGradeLevels,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorState.tutor]);

  useEffect(() => {
    // Handle "Tutor not found" gracefully - it's expected for new tutors
    if (tutorState.error && tutorState.error !== 'Tutor not found') {
      setOpenSnackbar(true);
      setSubmitStatus('error');
      setErrors((prev) => ({ ...prev, general: tutorState.error }));
      dispatch(clearTutor());
    } else if (tutorState.error === 'Tutor not found') {
      // Tutor profile doesn't exist yet - this is fine, just clear the error
      dispatch(clearTutor());
    } else if (tutorState.successMsg && tutorState.tutor) {
      setOpenSnackbar(true);
      setSubmitStatus('success');
      setTimeout(() => {
        dispatch(clearTutor());
        navigate('/tutor-profile');
      }, 2000);
    }
  }, [tutorState.error, tutorState.successMsg, tutorState.tutor, navigate, dispatch]);

  // Remove grade levels that have no subjects
  useEffect(() => {
    setFormData((prev) => {
      // Get all unique grade levels from subjects
      const gradeLevelsFromSubjects = [...new Set(prev.subjects.map(s => s.gradeLevel).filter(Boolean))];
      
      // Filter to only keep grade levels that have subjects
      const gradeLevelsWithSubjects = prev.gradeLevels.filter(gradeLevel => {
        return gradeLevelsFromSubjects.includes(gradeLevel);
      });
      
      // Only update if there's a change to avoid infinite loops
      const hasChanged = gradeLevelsWithSubjects.length !== prev.gradeLevels.length ||
        !gradeLevelsWithSubjects.every((g, i) => g === prev.gradeLevels[i]);
      
      if (hasChanged) {
        return {
          ...prev,
          gradeLevels: gradeLevelsWithSubjects,
        };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.subjects]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleGradeLevelToggle = (grade) => {
    setFormData((prev) => {
      const gradeLevels = prev.gradeLevels || [];
      const isSelected = gradeLevels.includes(grade);
      return {
        ...prev,
        gradeLevels: isSelected
          ? gradeLevels.filter((g) => g !== grade)
          : [...gradeLevels, grade],
      };
    });
  };

  const handlePredefinedSubjectToggle = (subject, gradeLevel) => {
    setFormData((prev) => {
      const subjects = prev.subjects || [];
      const existingIndex = subjects.findIndex(
        s => s.subject === subject && s.gradeLevel === gradeLevel
      );

      if (existingIndex >= 0) {
        // Remove if already exists
        return {
          ...prev,
          subjects: subjects.filter((_, index) => index !== existingIndex),
        };
      } else {
        // Add new subject
        return {
          ...prev,
          subjects: [...subjects, { subject, gradeLevel }],
        };
      }
    });
  };

  const handleCustomSubjectAdd = () => {
    if (customSubject.name.trim() && customSubject.gradeLevel) {
      setFormData((prev) => {
        const subjects = prev.subjects || [];
        const exists = subjects.some(
          s => s.subject.toLowerCase() === customSubject.name.trim().toLowerCase() 
            && s.gradeLevel === customSubject.gradeLevel
        );
        
        if (!exists) {
          return {
            ...prev,
            subjects: [...subjects, {
              subject: customSubject.name.trim(),
              gradeLevel: customSubject.gradeLevel,
            }],
          };
        }
        return prev;
      });
      setCustomSubject({ name: '', gradeLevel: '' });
      setCustomSubjectDialogOpen(false);
    }
  };

  const handleSubjectRemove = (subjectToRemove, gradeLevelToRemove) => {
    setFormData((prev) => {
      const updatedSubjects = prev.subjects.filter(
        s => !(s.subject === subjectToRemove && s.gradeLevel === gradeLevelToRemove)
      );
      
      // Check if there are any subjects left for this grade level
      const hasSubjectsForGrade = updatedSubjects.some(s => s.gradeLevel === gradeLevelToRemove);
      
      // Remove grade level from selection if no subjects remain
      const updatedGradeLevels = hasSubjectsForGrade
        ? prev.gradeLevels
        : prev.gradeLevels.filter(g => g !== gradeLevelToRemove);
      
      return {
        ...prev,
        subjects: updatedSubjects,
        gradeLevels: updatedGradeLevels,
      };
    });
  };

  const isSubjectSelected = (subject, gradeLevel) => {
    return formData.subjects.some(
      s => s.subject === subject && s.gradeLevel === gradeLevel
    );
  };

  const getSubjectsForGradeLevel = (gradeLevel) => {
    return formData.subjects.filter(s => s.gradeLevel === gradeLevel);
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.experience.trim()) {
      validationErrors.experience = t('tutorProfile.experienceRequired');
    }
    if (!formData.subjects.length) {
      validationErrors.subjects = t('tutorProfile.subjectsRequired');
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = () => {
    // Clear previous errors
    setErrors({});
    dispatch(clearTutor());
    
    if (validateForm()) {
      // Strip _id fields from subjects before sending to backend
      const cleanSubjects = formData.subjects.map(({ subject, gradeLevel }) => ({
        subject: String(subject),
        gradeLevel: String(gradeLevel)
      }));

      const data = new FormData();
      data.set('experience', JSON.stringify(formData.experience));
      data.set('subjects', JSON.stringify(cleanSubjects));
      data.set('gradeLevels', JSON.stringify(formData.gradeLevels));
      dispatch(updateTutor(data));
    } else {
      setOpenSnackbar(true);
      setSubmitStatus('error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/tutor-profile')}
          sx={{ mb: 3, color: theme.palette.primary.main }}
        >
          {t('common.back')}
        </Button>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            p: 4,
            background: 'white',
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Work sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {t('tutorProfile.experienceAndAvailability')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {errors.general && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {errors.general}
            </Alert>
          )}

          <Stack spacing={4}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {t('tutorProfile.experienceInstructions')}
            </Typography>

            <TextField
              rows={6}
              multiline
              label={t('tutorProfile.experience')}
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              error={Boolean(errors.experience)}
              helperText={errors.experience}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}
              >
                {t('tutorProfile.gradeLevel')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {t('tutorProfile.gradeLevelInstructions')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {gradeLevels.map((grade) => {
                  const isSelected = formData.gradeLevels?.includes(grade);
                  return (
                    <Chip
                      key={grade}
                      label={grade}
                      onClick={() => handleGradeLevelToggle(grade)}
                      sx={{
                        bgcolor: isSelected
                          ? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.1),
                        color: isSelected ? 'white' : theme.palette.primary.main,
                        border: `2px solid ${
                          isSelected
                            ? theme.palette.primary.main
                            : alpha(theme.palette.primary.main, 0.3)
                        }`,
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isSelected
                            ? theme.palette.primary.dark
                            : alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Subject sx={{ fontSize: 28, color: theme.palette.primary.main, mr: 1 }} />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                  >
                    {t('tutorProfile.subjects')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setCustomSubjectDialogOpen(true)}
                  sx={{
                    borderRadius: 2,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t('tutorProfile.addCustomSubject')}
                </Button>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {t('tutorProfile.subjectsInstructions')}
              </Typography>

              {formData.gradeLevels.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('tutorProfile.selectGradeLevelFirst')}
                </Alert>
              ) : (
                <Stack spacing={3}>
                  {formData.gradeLevels.map((gradeLevel) => {
                    const predefinedSubjects = PREDEFINED_SUBJECTS[gradeLevel] || [];
                    const selectedSubjects = getSubjectsForGradeLevel(gradeLevel);
                    
                    return (
                      <Box key={gradeLevel}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.primary.main }}
                        >
                          {gradeLevel}
                        </Typography>
                        
                        {/* Predefined Subjects */}
                        {predefinedSubjects.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '0.875rem' }}>
                              {t('tutorProfile.predefinedSubjects')}:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {predefinedSubjects.map((subject) => {
                                const isSelected = isSubjectSelected(subject, gradeLevel);
                                return (
                                  <Chip
                                    key={`${gradeLevel}-${subject}`}
                                    label={subject}
                                    onClick={() => handlePredefinedSubjectToggle(subject, gradeLevel)}
                                    onDelete={isSelected ? () => handleSubjectRemove(subject, gradeLevel) : undefined}
                                    sx={{
                                      bgcolor: isSelected
                                        ? theme.palette.primary.main
                                        : alpha(theme.palette.primary.main, 0.1),
                                      color: isSelected ? 'white' : theme.palette.primary.main,
                                      border: `2px solid ${
                                        isSelected
                                          ? theme.palette.primary.main
                                          : alpha(theme.palette.primary.main, 0.3)
                                      }`,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      '&:hover': {
                                        bgcolor: isSelected
                                          ? theme.palette.primary.dark
                                          : alpha(theme.palette.primary.main, 0.2),
                                      },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        {/* Selected Custom Subjects for this Grade Level */}
                        {selectedSubjects.filter(s => !predefinedSubjects.includes(s.subject)).length > 0 && (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '0.875rem' }}>
                              {t('tutorProfile.customSubjects')}:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {selectedSubjects
                                .filter(s => !predefinedSubjects.includes(s.subject))
                                .map((subjectObj) => (
                                  <Chip
                                    key={`${gradeLevel}-${subjectObj.subject}`}
                                    label={subjectObj.subject}
                                    onDelete={() => handleSubjectRemove(subjectObj.subject, gradeLevel)}
                                    sx={{
                                      bgcolor: theme.palette.primary.main,
                                      color: 'white',
                                      border: `2px solid ${theme.palette.primary.main}`,
                                      fontWeight: 600,
                                    }}
                                  />
                                ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}

              {errors.subjects && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
                  {errors.subjects}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/tutor-profile')}
                sx={{ borderRadius: 2 }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={tutorState.loading}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  minWidth: '150px',
                }}
              >
                {tutorState.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('common.save')
                )}
              </Button>
            </Box>
          </Stack>
        </Card>
      </Container>

      {/* Custom Subject Dialog */}
      <Dialog
        open={customSubjectDialogOpen}
        onClose={() => {
          setCustomSubjectDialogOpen(false);
          setCustomSubject({ name: '', gradeLevel: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {t('tutorProfile.addCustomSubject')}
            </Typography>
            <IconButton
              onClick={() => {
                setCustomSubjectDialogOpen(false);
                setCustomSubject({ name: '', gradeLevel: '' });
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('tutorProfile.subjectName')}
              value={customSubject.name}
              onChange={(e) => setCustomSubject({ ...customSubject, name: e.target.value })}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              select
              label={t('tutorProfile.gradeLevel')}
              value={customSubject.gradeLevel}
              onChange={(e) => setCustomSubject({ ...customSubject, gradeLevel: e.target.value })}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            >
              {gradeLevels.map((grade) => (
                <MenuItem key={grade} value={grade}>
                  {grade}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setCustomSubjectDialogOpen(false);
              setCustomSubject({ name: '', gradeLevel: '' });
            }}
            sx={{ borderRadius: 2 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCustomSubjectAdd}
            variant="contained"
            disabled={!customSubject.name.trim() || !customSubject.gradeLevel}
            sx={{
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
          >
            {t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={submitStatus === 'success' ? 'success' : 'error'}
          sx={{ borderRadius: 2 }}
        >
          {submitStatus === 'success'
            ? t('tutorProfile.profileUpdatedSuccess')
            : t('tutorProfile.profileUpdateFailed')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TutorExperienceForm;

